import MultiSelectInput from '@repo/ui/multiselect-input'
import { useQuery } from '@tanstack/react-query'

import { getFormulas } from '../../../../api/formula/get-formulas'
import { getPeriods } from '../../../../api/periods/get-periods'
import useTranslate from '../../../../lib/hooks/use-translate'
import { OlCommissionFilters } from '../../../../lib/types/models/ol/commission/ol-commission'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { t } from '../../../../locale/utils'
import { getLocalizedLabel } from '../../../../utils/functions/get-localized-label'

interface Props {
  filter?: OlCommissionFilters
  onFilterChange: (filter: OlCommissionFilters) => void
}

export function OlCommissionsFilter({ filter, onFilterChange }: Props) {
  const { currentLanguage } = useTranslate()
  function setFilter<K extends keyof OlCommissionFilters>(key: K, value: OlCommissionFilters[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  return (
    <div className={'flex flex-col gap-3'}>
      <div className="h-5" />
      <MultiSelectInput
        label={t('commissions.filters.formulas')}
        variant={'primary'}
        searchable
        options={formulas?.data.map((formula) => ({
          label: getTranslatedName(formula, currentLanguage) || '—',
          value: formula.id
        }))}
        selectedOptions={filter?.formulas?.map((formula) => {
          const selectedFormula = formulas?.data.find((f) => f.id === formula)
          return {
            label: selectedFormula
              ? getTranslatedName(selectedFormula, currentLanguage) || '—'
              : formula.toString(),
            value: formula
          }
        })}
        onChange={(formulas) => setFilter('formulas', formulas)}
      />

      <MultiSelectInput
        type="number"
        label={t('commissions.filters.durations.in.days')}
        variant="grey"
        onChange={(value) => setFilter('durationsInDays', value.map(Number))}
        selectedOptions={filter?.durationsInDays?.map((duration) => ({
          label: duration.toString(),
          value: duration.toString()
        }))}
      />

      <MultiSelectInput
        searchable
        label={t('commissions.filters.periods')}
        variant={'primary'}
        options={periods?.data.map((period) => ({
          label: getLocalizedLabel(period, currentLanguage, { fr: 'name', en: 'nameEn' }),
          value: period.id
        }))}
        selectedOptions={filter?.periods?.map((period) => {
          const selectedPeriod = periods?.data.find((p) => p.id === period)
          if (!selectedPeriod) return { label: period.toString(), value: period }

          return {
            label: getLocalizedLabel(selectedPeriod, currentLanguage, {
              fr: 'name',
              en: 'nameEn'
            }),
            value: period
          }
        })}
        onChange={(periods) => setFilter('periods', periods)}
      />
    </div>
  )
}
